/**
 * Reusable dialog shell: basic structure (modal, optional title bar, close button).
 * Content is passed as children – use MessageBoxContent or any custom component.
 */
import React from "react";
import { Modal, IconButton } from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";
import { getTheme, mergeStyleSets, FontWeights } from "@fluentui/react";
import { useTheme } from "../../theme/OAS365Theme";
import { useMetadata } from "../../redux/hooks/useMetadata";
import Tooltip from "../Tooltip/Tooltip";

const cancelIcon = { iconName: "Cancel" };

const iconButtonStyles = {
  root: {
    marginLeft: "auto",
    marginTop: 4,
    marginRight: 2,
    selectors: {
      ".ms-Button-icon": {
        fontSize: 12,
      },
    },
  },
};

/** Floating close (X) shown when the header is hidden but a dismiss control is still wanted. */
const floatingCloseButtonStyles = {
  root: {
    selectors: {
      ".ms-Button-icon": {
        fontSize: 12,
      },
    },
  },
};

const BORDER_BY_VARIANT = {
  error: "4px solid #a80000",
  warning: "4px solid #ffcc00",
  info: "4px solid #107c10",
  normal: "4px solid #a80000",
};

/** Top gradient line when showHeader is false (left to right: green to light green) */
const TOP_GRADIENT_LINE_STYLE = {
  height: 6,
  width: "100%",
  flex: "0 0 auto",
  background: "linear-gradient(90deg, rgb(36, 159, 93) 0%, rgb(226, 239, 223) 98.79%)",
};

/** Yellow top bar for error/warning state (e.g. check-in error dialog) */
const TOP_LINE_ERROR_STYLE = {
  height: 6,
  width: "100%",
  flex: "0 0 auto",
  background: "#ffcc00",
};

/**
 * @param {boolean} isOpen - Whether the dialog is visible
 * @param {() => void} onClose - Called when close (X) is clicked
 * @param {string} [title] - Optional title in the header (when showHeader is true)
 * @param {boolean} [showHeader=true] - Show/hide header row (title + close icon)
 * @param {'error'|'warning'|'info'|'normal'} [variant='normal'] - Header border color
 * @param {boolean} [isBlocking=false] - Modal blocking behavior
 * @param {boolean} [centerContent=true] - Center content in dialog body (ignored when titleContent/actions used)
 * @param {number|string} [dialogMinHeight=220] - Minimum modal height
 * @param {number|string} [dialogMinWidth] - Minimum modal width (e.g. '400px')
 * @param {number|string} [dialogMaxWidth] - Maximum modal width (e.g. 'min(92vw, 640px)') so content cannot expand the shell
 * @param {number|string} [bodyMinHeight=140] - Minimum body content area height
 * @param {number|string} [dialogMaxHeight] - Max height of dialog (e.g. '90vh') so only body scrolls, not full dialog
 * @param {number|string} [bodyMaxHeight] - Max height of scrollable body area (e.g. '60vh') when using titleContent/actions
 * @param {boolean} [bodyFlexColumn=false] - When true, body is flex column (auto when titleContent/actions passed)
 * @param {React.ReactNode} [titleContent] - Optional component/node for title area (fixed, no scroll)
 * @param {React.ReactNode} [actions] - Optional component/node for actions area (fixed, no scroll)
 * @param {'default'|'error'} [topBarVariant='default'] - When showHeader is false: 'default' = green gradient top line, 'error' = yellow top bar
 * @param {boolean} [showCloseIcon=false] - When showHeader is false, render a floating close (X) at the top-right so a header-less dialog can still be dismissed
 * @param {() => void} [onCloseIcon] - Handler for the floating close icon (defaults to onClose)
 * @param {string} [closeIconTooltip] - Tooltip shown when hovering the floating close icon
 * @param {React.ReactNode} children - Main body content (scrollable when titleContent or actions is passed)
 */
export function ReusableDialog({
  isOpen,
  onClose,
  title = "",
  showHeader = true,
  hideHeaderDivider = false,
  variant = "normal",
  topBarVariant = "default",
  showCloseIcon = false,
  onCloseIcon,
  closeIconTooltip,
  isBlocking = false,
  centerContent = true,
  dialogMinHeight = 220,
  dialogMinWidth,
  dialogMaxWidth,
  bodyMinHeight = 140,
  bodyPadding = "16px 24px 12px",
  dialogMaxHeight,
  bodyMaxHeight,
  bodyFlexColumn = false,
  titleContent,
  actions,
  children,
}) {
  const hasSlots = titleContent != null || actions != null;
  const useFlexColumn = bodyFlexColumn || hasSlots;
  const themeConfig = useTheme();
  const metadata = useMetadata();
  const theme = getTheme();
  const titleId = useId("reusable-dialog-title");
  const viewWidth = metadata?.isMainCompWidthChange ?? 0;
  const isDesktop = viewWidth > 991;
  const sidebarVisible = metadata?.hideSideBar !== true;
  const shiftRightPx = isDesktop && sidebarVisible ? 100 : 0;

  const contentStyles = mergeStyleSets({
    container: {
      display: "flex",
      flexFlow: "column nowrap",
      alignItems: "stretch",
      ...(dialogMaxHeight && { maxHeight: dialogMaxHeight, overflow: "hidden" }),
      selectors: {
        ".ms-Modal-scrollableContent": {
          backgroundColor: themeConfig.colors.backgroundColor,
          color: themeConfig.colors.fontColor,
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
          ...(dialogMaxHeight && { maxHeight: dialogMaxHeight, overflow: "hidden" }),
        },
      },
    },
    header: [
      theme.fonts.xLargePlus,
      {
        flex: "1 1 auto",
        borderTop: "none",
        position: "relative",
        display: "flex",
        alignItems: "center",
        fontFamily: themeConfig.fonts.regular,
        fontWeight: FontWeights.semibold,
        fontSize: themeConfig.fontSizes.medium,
        padding: "12px 12px 10px 24px",
        selectors: {
          ...(hideHeaderDivider
            ? {}
            : {
                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: "16px",
                  right: "16px",
                  bottom: 0,
                  borderBottom: `1px solid ${themeConfig.colors.dividerColor ?? "#DCDCDC"}`,
                },
              }),
        },
      },
    ],
    body: {
      flex: "4 4 auto",
      fontFamily: themeConfig.fonts.regular,
      padding: bodyPadding,
      overflowY: "hidden",
      overflowX: "hidden",
      minHeight: bodyMinHeight,
      minWidth: 0,
      maxWidth: "100%",
      boxSizing: "border-box",
    },
  });

  const bodyStyle = useFlexColumn
    ? {
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        paddingTop: showHeader ? undefined : "12px",
      }
    : centerContent
      ? {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          paddingTop: showHeader ? undefined : "12px",
        }
      : showHeader
        ? undefined
        : {
            paddingTop: "12px",
          };

  const titleAreaStyle = {
    flex: "0 0 auto",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
  };
  const scrollableBodyStyle = {
    flex: "1 1 auto",
    minHeight: 0,
    minWidth: 0,
    // Only scroll when capped (e.g. long details); otherwise avoid an empty scrollbar gutter.
    overflowY: bodyMaxHeight ? "auto" : "visible",
    overflowX: "hidden",
    ...(bodyMaxHeight && { maxHeight: bodyMaxHeight }),
  };
  const actionsAreaStyle = {
    flex: "0 0 auto",
  };

  // When the header-less floating close icon is the only focusable control, the
  // modal auto-focuses it on mount, which makes its (focus-triggered) tooltip pop
  // up before the user hovers. Skip the initial auto-focus in that case.
  const floatingCloseIconOnly = !showHeader && showCloseIcon;

  return (
    <Modal
      titleAriaId={titleId}
      isOpen={isOpen}
      isBlocking={isBlocking}
      containerClassName={contentStyles.container}
      {...(floatingCloseIconOnly && {
        focusTrapZoneProps: { disableFirstFocus: true },
      })}
      styles={{
        main: {
          position: "relative",
          minHeight: dialogMinHeight,
          ...(dialogMinWidth && { minWidth: dialogMinWidth }),
          ...(dialogMaxWidth && { maxWidth: dialogMaxWidth }),
          boxSizing: "border-box",
          ...(dialogMaxHeight && { maxHeight: dialogMaxHeight, overflow: "hidden" }),
          display: "flex",
          flexDirection: "column",
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
          overflow: "hidden",
          transform: shiftRightPx ? `translateX(${shiftRightPx}px)` : undefined,
          transition: "transform 0.15s ease-in-out",
        },
      }}
    >
      {((topBarVariant ?? "default") === "error" || (topBarVariant ?? "default") === "default") && (
        <div
          style={(topBarVariant ?? "default") === "error" ? TOP_LINE_ERROR_STYLE : TOP_GRADIENT_LINE_STYLE}
          aria-hidden="true"
        />
      )}
      {showHeader && (
        <div className={contentStyles.header}>
          <span id={titleId}>{title}</span>
          <IconButton
            styles={iconButtonStyles}
            iconProps={cancelIcon}
            ariaLabel="Close"
            onClick={onClose}
          />
        </div>
      )}
      {!showHeader && showCloseIcon && (
        <div style={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
          <Tooltip content={closeIconTooltip} position="left">
            <IconButton
              styles={floatingCloseButtonStyles}
              iconProps={cancelIcon}
              ariaLabel="Close"
              onClick={onCloseIcon ?? onClose}
            />
          </Tooltip>
        </div>
      )}
      <div className={contentStyles.body} style={bodyStyle}>
        {hasSlots ? (
          <>
            {titleContent != null && <div style={titleAreaStyle}>{titleContent}</div>}
            <div style={scrollableBodyStyle}>{children}</div>
            {actions != null && <div style={actionsAreaStyle}>{actions}</div>}
          </>
        ) : (
          children
        )}
      </div>
    </Modal>
  );
}

export default ReusableDialog;
